import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { InvestigatorNoteCategory } from '../interfaces/InvestigatorNotes';
import investigatorNotesServices from '../services/investigatorNotes';

export const fetchAllInvestigatorNoteCategories = createAsyncThunk<
InvestigatorNoteCategory[],
number,
{ rejectValue: string }
>('note-categories', async (page: number, { dispatch, rejectWithValue }) => {
  try {
    const response = await investigatorNotesServices.fetchNotesCategory(page);

    if (response.next) {
      const nextPage = page + 1;
      dispatch(fetchAllInvestigatorNoteCategories(nextPage));
    }

    return response.results;
  } catch (error) {
    return rejectWithValue('Failed to fetch note categories');
  }
});

export type ReportType = {
  value: number;
  label: string;
};

type FormFieldsType = {
  recipientName: string;
  recipientEmail: string;
  subject: string;
  dueDate: string | unknown;
  hasRequestToUploadFile: boolean;
  department: string;
  content: string;
};

type SendRequestType = {
  open: boolean;
  message: string;
  severity: string;
};

export interface MeetingTranscript {
  id: string;
  title: string;
  createdAt: string;
  transcriptURL: string;
  audioSource: string | string[];
}


export type GetCaseManagerDataTypeFile = {
  id: number;
  original_file_name: string;
  file_type: string;
  url: string;
};

export type RecipientDetailsType = {
  id: number;
  recipient_email: string;
  recipient_name: string;
  department_name: string;
  has_request_to_upload_file: boolean;
  details: string;
  due_date: string;
  subject: string;
  files: Array<GetCaseManagerDataTypeFile> | null;
  status: number;
  closure_reason: string;
};

export type ReportState = {
  departments: Array<ReportType>;
  departmentsLoading: boolean;
  currentReport: string;
  currentReportReference: string;
  viewRequestLoading: boolean;
  currrentReportForm: FormFieldsType;
  currentReportFormSecuredUrl: string;
  items: Array<{
    case_manager: string;
    department_name: string;
    due_date_formatted_date: string;
    id: number;
    status: number;
  }>;
  sendRequest: SendRequestType;
  hasUnreadMessages: boolean;
  meetingTranscripts: MeetingTranscript[];
  investigatorNoteCategories: InvestigatorNoteCategory[];
  currentRequestInformationItem: RecipientDetailsType;
};

const initialState: ReportState = {
  viewRequestLoading: false,
  hasUnreadMessages: true,
  departments: [],
  sendRequest: {
    open: false,
    message: '',
    severity: 'success',
  },
  departmentsLoading: true,
  currentReport: '',
  currentReportReference: '',
  items: [],
  currrentReportForm: {
    recipientName: '',
    recipientEmail: '',
    subject: '',
    dueDate: '',
    hasRequestToUploadFile: false,
    department: '',
    content:
      '<p>Dear {name},</p><p></p><p>I hope this message finds you well. Your whistleblowing investigations team is reaching out to request your assistance in providing specific information that is crucial for a case they are currently managing.</p><p></p><p>{Enter Due Date for Response}</p><p>[Brief Description of the Information Needed]</p><p/></p><p></p><p>To facilitate a secure and efficient response, we have set up a dedicated interface on our Confide platform. Please click on the link below to access this platform and respond to the request:</p><p></p><p></p><p id="securedPortalUrl">[Secure Link to Confide Platform]</p><p></p><p></p><p>Your prompt and detailed response to this request would be greatly appreciated and will significantly aid in the progress of this case.</p><p></p><p></p><p>Should you have any questions or require further clarification, please do not hesitate to contact me directly.</p><p></p><p></p><p>Thank you in advance for your cooperation and support.</p><p></p><p></p><p>Warm regards,</p><p></p><p></p>',
  },
  currentReportFormSecuredUrl: '',
  currentRequestInformationItem: {
    id: 0,
    due_date: '',
    subject: '',
    recipient_email: '',
    recipient_name: '',
    department_name: '',
    has_request_to_upload_file: false,
    details: '',
    files: [],
    status: 0,
    closure_reason: '',
  },
  meetingTranscripts: [],
  investigatorNoteCategories: [],
};

export const reportSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    setUnreadMessages: (state, action: PayloadAction<boolean>) => {
      state.hasUnreadMessages = action.payload;
    },
    setCurrentReportFormSecuredUrl: (state, action: PayloadAction<string>) => {
      state.currentReportFormSecuredUrl = action.payload;
    },
    setViewRequestLoading: (state, action: PayloadAction<boolean>) => {
      state.viewRequestLoading = action.payload;
    },
    setSendRequest: (state, action: PayloadAction<SendRequestType>) => {
      state.sendRequest = action.payload;
    },
    setDepartments: (state, action: PayloadAction<Array<ReportType>>) => {
      state.departments = action.payload;
    },
    setDeparmentsLoading: (state, action: PayloadAction<boolean>) => {
      state.departmentsLoading = action.payload;
    },
    setCurrentReportReference: (state, action: PayloadAction<string>) => {
      state.currentReportReference = action.payload;
    },
    setCurrentReport: (state, action: PayloadAction<string>) => {
      state.currentReport = action.payload;
    },
    setCurrentReportForm: (state, action: PayloadAction<FormFieldsType>) => {
      state.currrentReportForm = action.payload;
    },
    setItems: (
      state,
      action: PayloadAction<
      Array<{
        case_manager: string;
        department_name: string;
        due_date_formatted_date: string;
        id: number;
        status: number;
      }>
      >,
    ) => {
      state.items = action.payload;
    },
    setMeetingTranscripts: (
      state,
      action: PayloadAction<MeetingTranscript[]>,
    ) => {
      state.meetingTranscripts = action.payload;
    },
    setCurrentRequestInformationItem: (
      state,
      action: PayloadAction<RecipientDetailsType>,
    ) => {
      state.currentRequestInformationItem = action.payload;
    },
    setInvestigatorNoteCategories: (
      state,
      action: PayloadAction<InvestigatorNoteCategory[]>,
    ) => {
      state.investigatorNoteCategories = action.payload;
    },
    setInvestigatorNoteCategoriesAsActive: (
      state,
      action: PayloadAction<InvestigatorNoteCategory>,
    ) => {
      state.investigatorNoteCategories = state.investigatorNoteCategories.map(
        (category) => {
          if (category.id === action.payload.id) {
            return action.payload;
          }
          return category;
        },
      );
    },
    removeInvestigatorNoteCategory: (state, action: PayloadAction<number>) => {
      state.investigatorNoteCategories =
        state.investigatorNoteCategories.filter(
          (category) => category.id !== action.payload,
        );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      fetchAllInvestigatorNoteCategories.fulfilled,
      (state, action: PayloadAction<InvestigatorNoteCategory[]>) => {
        state.investigatorNoteCategories =
          state.investigatorNoteCategories.concat(action.payload);
      },
    );
  },
});

// Action creators are generated for each case reducer function
export const {
  setDepartments,
  setDeparmentsLoading,
  setCurrentReport,
  setCurrentReportReference,
  setSendRequest,
  setCurrentReportForm,
  setViewRequestLoading,
  setUnreadMessages,
  setCurrentReportFormSecuredUrl,
  setItems,
  setMeetingTranscripts,
  setCurrentRequestInformationItem,
  setInvestigatorNoteCategories,
  setInvestigatorNoteCategoriesAsActive,
  removeInvestigatorNoteCategory,
} = reportSlice.actions;

export default reportSlice.reducer;
